'use client';

import { ComponentPropsWithoutRef, forwardRef } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Box, SxProps, TextareaAutosize, type Theme } from '@mui/material';
// eslint-disable-next-line no-restricted-imports
import { TextFieldProps as BaseMuiTextFieldProps } from '@mui/material/TextField';

import clsx from 'clsx';
import { useField } from 'formik';
import omit from 'lodash/omit';

import SearchIcon from '@watershed/icons/components/Search';
import mixinSx from '@watershed/style/mixinSx';
import { getPaletteUtils } from '@watershed/style/styleUtils';

import type { FieldProps } from './Field';
import Field, { filterFieldInputProps } from './Field';

/**
 * These exclude deprecated props. Use slots/slotProps instead for those:
 * https://mui.com/material-ui/migration/migrating-from-deprecated-apis/#textfield
 */
export type MuiTextFieldProps = Omit<
  BaseMuiTextFieldProps,
  | 'InputProps'
  | 'inputProps'
  | 'SelectProps'
  | 'InputLabelProps'
  | 'FormHelperTextProps'
>;

const inputSx: SxProps<Theme> = (theme) => {
  const paletteUtils = getPaletteUtils(theme.palette);
  return {
    ...paletteUtils.textFieldStyles,
    margin: 0,
    width: '100%',
    resize: 'none',
    '&:user-invalid': {
      boxShadow: paletteUtils.boxShadowField.error,
    },
  };
};

const inputSmallSx: SxProps<Theme> = (theme) => {
  const inputSmallFontStyles = omit(theme.typography.body3, 'color');
  return {
    ...inputSmallFontStyles,
    padding: theme.spacing(0.5, 1),
  };
};

const inputWarningSx: SxProps<Theme> = (theme) => {
  const paletteUtils = getPaletteUtils(theme.palette);
  return {
    boxShadow: paletteUtils.boxShadowField.warning,
  };
};

const inputErrorSx: SxProps<Theme> = (theme) => {
  const paletteUtils = getPaletteUtils(theme.palette);
  return {
    boxShadow: paletteUtils.boxShadowField.error,
  };
};

const searchMediumSx: SxProps<Theme> = (theme) => ({
  '& input': {
    paddingLeft: '36px',
    '&::-webkit-search-decoration': { WebkitAppearance: 'none' },
    // Styles for the search cancel button - note this has limited support across browsers.
    '&::-webkit-search-cancel-button': {
      WebkitAppearance: 'none',
      width: '16px',
      height: '16px',
      // This a x icon.
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${theme.palette.grey70.replace(
        '#',
        ''
      )}'><path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/></svg>")`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& > svg': {
    position: 'absolute',
    bottom: '8px',
    left: '10px',
  },
});

const searchSmallSx: SxProps<Theme> = (theme) => ({
  '& input': {
    paddingLeft: '28px',
    '&::-webkit-search-decoration': { WebkitAppearance: 'none' },
    '&::-webkit-search-cancel-button': {
      WebkitAppearance: 'none',
      width: '12px',
      height: '12px',
      // This a x icon.
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23${theme.palette.grey70.replace(
        '#',
        ''
      )}'><path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z'/></svg>")`,
      backgroundSize: '100% 100%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
  },
  '& > svg': {
    position: 'absolute',
    bottom: '6px',
    left: '8px',
  },
});

const resizeNoneSx: SxProps<Theme> = {
  resize: 'none',
};

export type TextFieldCommonProps = {
  id: string;
  size?: 'small' | 'medium';
  fsUnmask?: boolean;
  name?: string;
};

export interface TextFieldProps
  extends Omit<FieldProps, 'inputId'>,
    Omit<
      ComponentPropsWithoutRef<'input'>,
      'size' | keyof TextFieldCommonProps
    >,
    TextFieldCommonProps {}

export interface TextFieldMultilineProps
  extends Omit<FieldProps, 'inputId'>,
    Omit<ComponentPropsWithoutRef<'textarea'>, keyof TextFieldCommonProps>,
    TextFieldCommonProps {
  disableResize?: boolean;
  maxRows?: number;
}

function getInputProps(props: TextFieldProps | TextFieldMultilineProps) {
  const { id, validationState, validationMessage, sublabel, required, value } =
    props;

  const result: Record<string, any> = {
    id,
    className: props.fsUnmask ? 'fs-unmask' : undefined,
    sx: mixinSx(
      inputSx,
      'size' in props && props.size === 'small' && inputSmallSx,
      validationState === 'error' && inputErrorSx,
      validationState === 'warning' && inputWarningSx,
      'disableResize' in props && props.disableResize && resizeNoneSx
    ),
    'aria-invalid': validationState === 'error',
    'aria-required': required,
    'aria-describedby': clsx(
      sublabel ? `${id}-sublabel` : undefined,
      validationMessage ? `${id}-validationMessage` : undefined
    ),
    required,
  };
  // Stringify the value, because `number` is incompatible with
  // `react-expanding-textarea`. Don't set this, though, unless props.value is
  // defined, so we don't inadvertently override formikProps.value.
  if (value != null) {
    result.value = String(value);
  }
  return result;
}

/** When `type="number"`, the default browser behavior is to edit the value
 * (!!!). Ideally, we wouldn't use `type="number"`, but we still have needs for
 * the min/max properties there — so this is a hopefully-temporary fix: if you
 * scroll on a focused number input, we blur the input rather than change the
 * value. That way the page scrolls, and the field remains unedited. (We tried
 * e.preventDefault and e.stopPropagation and forwarding scroll events but those
 * didn't do the trick.)
 *
 * TODO: We'd like to create a TextFieldNumeric component that has the same
 * min/max properties as the native number input, but doesn't have the
 * many-other-problems that come with the native number input.
 **/
const onWheelHandlerToPreventChangingValue =
  (props: TextFieldProps) => (e: any) => {
    if (props.type === 'number') {
      e.currentTarget.blur();
    }
    props.onWheel?.(e);
  };

const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextField(
    {
      children,
      size = 'medium',
      className,
      style,
      ...rawProps
    }: TextFieldProps,
    ref
  ) {
    const [formikProps, meta] = useField({
      name: rawProps.name ?? rawProps.id,
      ...rawProps,
    });

    const props: TextFieldProps = {
      validationState: meta.touched && meta.error ? 'error' : 'default',
      validationMessage: meta.touched && meta.error ? meta.error : undefined,
      size,
      ...rawProps,
    };

    // If we spread all the props/raw props without taking out the `fsUnmask`
    // prop, it gets passed to the DOM element and React complains loudly that
    // it's not a real prop. Which is true. So we remove it here. It's handled
    // by `getInputProps`'s `className` prop and nothing else needs to use it.
    const rawPropsWithoutFsUnmask = omit(rawProps, 'fsUnmask');
    const propsWithoutFsUnmask = omit(props, 'fsUnmask');
    return (
      <Field
        {...propsWithoutFsUnmask}
        inputId={rawProps.id}
        className={className}
      >
        <Box
          component="input"
          data-testid={`textField-${rawProps.id}`}
          ref={ref}
          onWheel={onWheelHandlerToPreventChangingValue(rawProps)}
          {...formikProps}
          {...filterFieldInputProps(rawPropsWithoutFsUnmask)}
          {...getInputProps(props)}
          style={style}
        />
        {children}
      </Field>
    );
  }
);

export default TextField;

export const TextFieldNonFormik = forwardRef<HTMLInputElement, TextFieldProps>(
  function TextFieldNonFormik(
    { children, size = 'medium', className, style, fsUnmask, ...props },
    ref
  ) {
    return (
      <Field {...props} inputId={props.id} className={className}>
        <Box
          component="input"
          ref={ref}
          {...filterFieldInputProps(props)}
          {...getInputProps({ size, fsUnmask, ...props })}
          style={style}
          onWheel={onWheelHandlerToPreventChangingValue(props)}
        />
        {children}
      </Field>
    );
  }
);

export interface TextFieldSearchProps extends Omit<TextFieldProps, 'label'> {
  label?: LocalizedString;
  showSearchIcon?: boolean;
}

/**
 * A search field with a search icon.
 */
export const TextFieldSearch = forwardRef<
  HTMLInputElement,
  TextFieldSearchProps
>(function TextFieldSearch(
  {
    label,
    visuallyHideLabel = true,
    className,
    size = 'medium',
    showSearchIcon = true,
    ...props
  },
  ref
) {
  const { t } = useLingui();
  label ??= t({
    message: 'Search',
    context: 'Default placeholder for search field',
  });

  return (
    <TextFieldNonFormik
      data-testid={`textFieldSearch-${props.id}`}
      ref={ref}
      visuallyHideLabel={visuallyHideLabel}
      placeholder={label}
      {...props}
      size={size}
      label={label}
      type="search"
      sx={mixinSx(size === 'small' ? searchSmallSx : searchMediumSx, props.sx)}
      className={className}
    >
      {showSearchIcon && (
        <SearchIcon color={(theme) => theme.palette.secondary.dark} />
      )}
    </TextFieldNonFormik>
  );
});

export const TextFieldMultiline = forwardRef<
  HTMLTextAreaElement,
  TextFieldMultilineProps
>(function TextFieldMultiline({ children, ...rawProps }, ref) {
  const [formikProps, meta] = useField({
    name: rawProps.name ?? rawProps.id,
    ...rawProps,
  });

  const props: TextFieldMultilineProps = {
    validationState: meta.touched && meta.error ? 'error' : 'default',
    validationMessage: meta.touched && meta.error ? meta.error : undefined,
    ...rawProps,
  };

  return (
    <Field {...props} inputId={rawProps.id}>
      <Box
        component={TextareaAutosize}
        data-testid={`textField-${props.id}`}
        ref={ref}
        {...formikProps}
        {...filterFieldInputProps(rawProps)}
        {...getInputProps(props)}
        minRows={props.rows ?? 2}
        maxRows={props.maxRows}
      />
      {children}
    </Field>
  );
});

export const TextFieldMultilineNonFormik = forwardRef<
  HTMLTextAreaElement,
  TextFieldMultilineProps
>(function TextFieldMultilineNonFormik(
  { children, ...props }: TextFieldMultilineProps,
  ref
) {
  const spreadProps: ReturnType<typeof filterFieldInputProps> &
    ReturnType<typeof getInputProps> = {
    ...filterFieldInputProps(props),
    ...getInputProps(props),
  };
  if (spreadProps.value != null) {
    spreadProps.value = String(spreadProps.value);
  }
  return (
    <Field {...props} inputId={props.id}>
      <Box
        component={TextareaAutosize}
        data-testid={`textField-${props.id}`}
        ref={ref}
        {...spreadProps}
        minRows={props.rows ?? 2}
      />
      {children}
    </Field>
  );
});
